import FormattedHTMLMessage from 'components/FormattedHTMLMessage';
import {parseMessage} from 'lib/stringHelper';
import {last} from 'lodash';
import {getTasksInCard} from 'lib/cardAndTaskHelper';
import {calculateRequiredTipsForCard} from 'lib/protocolHelper';
import {
  CardType,
  PipettingParameterFieldKey,
  TaskType,
  TipChannelType,
  TrayType,
  ViewMode,
} from '../../constants/dataEnum';
import jsonCopy from '../../lib/jsonCopy';
import {filter, filterToArray, find} from '../../lib/objectHelper';
import {hideLoading, showLoading} from '../actions/appStateAction';
// eslint-disable-next-line import/no-cycle
import {showModalSimplified} from './commonModalBusiness';

export function getTipCount(tipChannel) {
  let rowTipCount;
  let columnTipCount;

  switch (tipChannel) {
    case TipChannelType.TIP_CHANNEL_1: {
      rowTipCount = 1;
      columnTipCount = 1;
      break;
    }
    case TipChannelType.TIP_CHANNEL_8: {
      rowTipCount = 8;
      columnTipCount = 1;
      break;
    }
    case TipChannelType.TIP_CHANNEL_12: {
      rowTipCount = 1;
      columnTipCount = 12;
      break;
    }
    case TipChannelType.TIP_CHANNEL_96:
      rowTipCount = 8;
      columnTipCount = 12;
      break;
    default:
      break;
  }

  return [rowTipCount, columnTipCount];
}

export function getNewCardId(cards, initCheckPointId) {
  let cardId = initCheckPointId;
  while (cards[cardId]) {
    cardId += 1;
  }
  return cardId;
}

function getUncompleteCard(cards, tasks, rowTipCount, columnTipCount) {
  let uncompleteCard;
  // eslint-disable-next-line no-restricted-syntax
  for (const card of Object.values(cards)) {
    if (card.type === CardType.SIMPLE_TRANSFER && !card.isIgnored) {
      const ejectTipTask = find(tasks, {
        ID: card?.tasks,
        type: TaskType.EJECT_TIP,
      });

      if (!ejectTipTask) {
        const hasSamePipettingChannel = find(tasks, {
          ID: card?.tasks,
          rowTipCount,
          columnTipCount,
        });

        const hasUncompleteMixingTask = find(tasks, {
          ID: card?.tasks,
          type: TaskType.MIXING,
          labwareID: undefined,
        });

        if (hasSamePipettingChannel || hasUncompleteMixingTask) {
          uncompleteCard = card;
          break;
        }
      }
    }
  }
  return uncompleteCard;
}

export function generateCardIDAndTaskIDAndNumber(
  cards,
  tasks,
  cardType,
  // eslint-disable-next-line default-param-last
  cardId = null,
  // eslint-disable-next-line default-param-last
  taskType = '',
  selectedTipChannel,
  // eslint-disable-next-line default-param-last
  customTipCount = null,
  groups,
) {
  let cardID = 0;
  let taskID = 0;
  let currentCardId = cardId;
  const selectedCards = filter(cards, {isSelected: true});
  const lastSelectedCard = last(Object.values(selectedCards));
  const selectedGroup = find(groups, (item) => item?.ID === lastSelectedCard?.groupID && item?.isSelected);
  const isAddingMixingTaskInST = taskType === TaskType.MIXING;

  if (cardId === null) {
    if (lastSelectedCard) currentCardId = lastSelectedCard.ID;
    else if (Object.keys(cards).length > 0) currentCardId = Math.max(...Object.keys(cards));
  }

  if (Object.keys(tasks).length > 0) {
    taskID = Math.max(...Object.values(tasks).map((item) => item.ID)) + 1;
  }

  if (cardType === CardType.SERIAL_DILUTION) {
    if (lastSelectedCard?.sdRawTasks && Object.keys(lastSelectedCard.sdRawTasks).length > 0) {
      taskID = Math.max(...Object.values(lastSelectedCard.sdRawTasks).map((item) => item.ID)) + 1;
    } else {
      taskID = 1;
    }
  }

  // eslint-disable-next-line no-shadow
  const insertTask = find(tasks, {type: TaskType.INSERT_TASK});
  const lastCard = cards[currentCardId];

  let rowTipCount;
  let columnTipCount;
  let uncompleteCard;
  let groupId;

  if (cardType === CardType.SIMPLE_TRANSFER) {
    if (customTipCount) {
      rowTipCount = customTipCount.row;
      columnTipCount = customTipCount.column;
    } else {
      [rowTipCount, columnTipCount] = getTipCount(selectedTipChannel);
    }

    uncompleteCard = getUncompleteCard(cards, tasks, rowTipCount, columnTipCount);
  }

  if (lastCard) {
    cardID = currentCardId;

    if (lastCard.type === cardType) {
      switch (cardType) {
        case CardType.SIMPLE_TRANSFER: {
          // region - create new card if card already has pick tip
          const pickTipsTask = find(tasks, {
            ID: lastCard?.tasks,
            type: TaskType.PICK_TIP,
          });

          if (taskType === TaskType.PICK_TIP && pickTipsTask && !insertTask && !isAddingMixingTaskInST) {
            cardID = getNewCardId(cards, currentCardId);
            break;
          }
          // endregion

          // region - create new card if card already has eject tip and there is any uncomplete card
          const ejectTipsTask = find(tasks, {
            ID: lastCard?.tasks,
            type: TaskType.EJECT_TIP,
          });

          if (!insertTask && ejectTipsTask && !uncompleteCard && !isAddingMixingTaskInST) {
            cardID = getNewCardId(cards, currentCardId);
            break;
          }
          // endregion

          // region - there is uncomplete card with same pipetting channel
          if (!insertTask && ejectTipsTask && uncompleteCard) {
            cardID = uncompleteCard.ID;
            break;
          }
          // endregion

          const uncompleteMixingTask = find(tasks, {
            ID: lastCard?.tasks,
            type: TaskType.MIXING,
            labwareID: undefined,
          });

          // region - there is uncomplete card with same pipetting channel
          if (!insertTask && uncompleteCard && uncompleteMixingTask && isAddingMixingTaskInST) {
            cardID = uncompleteCard.ID;
            break;
          }
          // endregion

          // region - create new card if pipeting channel is not match
          const hasSamePipettingChannel = find(tasks, {
            ID: lastCard?.tasks,
            rowTipCount,
            columnTipCount,
          });

          if (taskType && !hasSamePipettingChannel) {
            cardID = getNewCardId(cards, currentCardId);
          }
          // endregion

          break;
        }
        default:
          if (
            lastSelectedCard?.ID !== currentCardId ||
            lastSelectedCard?.type === CardType.DELAY ||
            lastSelectedCard?.type === CardType.CONFIRMATION ||
            lastSelectedCard?.type === CardType.MIXING
          ) {
            cardID = getNewCardId(cards, currentCardId);
          }
          break;
      }
    } else if (uncompleteCard) {
      cardID = uncompleteCard.ID;
    } else {
      groupId = selectedGroup ? null : lastSelectedCard?.groupID; // if group is selected then don't include this card inner group and vice versa.
      cardID = getNewCardId(cards, currentCardId);
    }
  }

  // eslint-disable-next-line no-unsafe-optional-chaining
  const number = cards[cardID]?.tasks.length ? cards[cardID]?.tasks.length + 1 : 1; // number: start from 1, inclusive in the card

  return [cardID, taskID, number, groupId];
}

export function getNewCardName(cards, cardType) {
  // eslint-disable-next-line arrow-body-style
  const associatedCards = filterToArray(cards, ({name, type}) => {
    return type === cardType && String(name).includes(cardType);
  });

  if (!associatedCards.length) {
    return `${cardType} 1`;
  }

  const splittedPrefix = cardType.split(' ');

  const maxCardIndex = associatedCards.reduce((max, card) => {
    const splittedNames = card.name.split(' ');
    const index = parseInt(splittedNames[splittedPrefix.length], 10);

    if (Number.isNaN(index)) {
      return max;
    }

    return Math.max(max, index);
  }, 1);

  return `${cardType} ${maxCardIndex + 1}`;
}

export const convertRowToAlphabet = (row) => String.fromCharCode(64 + parseInt(row, 10));

export const onReduceNormalizationTask = (taskIds, cardId, cards, tasks) => {
  let newTasks = jsonCopy(tasks);
  const newCards = jsonCopy(cards);

  let aspirateTask = {};
  let dispenseTask = {};
  let pickTipTask = {};
  let newNormalizationTasks = {};

  const newTaskIds = [];
  const totalTask = taskIds.length;
  const firstTaskID = cards[cardId].tasks[0];
  const dispenseTaskByWell = {};

  for (let i = 0; i < totalTask; i += 1) {
    const task = newTasks[i + firstTaskID];

    switch (task.type) {
      case TaskType.ASPIRATE: {
        aspirateTask = {
          ...task,
          ID: pickTipTask.ID + 1,
          number: pickTipTask.number + 1,
        };
        break;
      }
      case TaskType.DISPENSE: {
        dispenseTask = {
          ...task,
          ID: aspirateTask.ID + 1,
          number: aspirateTask.number + 1,
        };

        const rowCol = `${task.row}${task.column}`;
        const existingDispenseWell = dispenseTaskByWell[rowCol];

        if (existingDispenseWell) {
          const {aspirateId, dispenseId} = existingDispenseWell;
          newNormalizationTasks[aspirateId].volume += task.volume;
          newNormalizationTasks[dispenseId].volume += task.volume;
          i += 1; // skips eject tip
        } else {
          dispenseTaskByWell[rowCol] = {
            ...task,
            aspirateId: aspirateTask.ID,
            dispenseId: dispenseTask.ID,
          };
        }
        break;
      }
      case TaskType.EJECT_TIP: {
        const ejectTip = {
          ...task,
          ID: dispenseTask.ID + 1,
          number: dispenseTask.number + 1,
          changeTip: true,
        };

        newNormalizationTasks = {
          ...newNormalizationTasks,
          [pickTipTask.ID]: pickTipTask,
          [aspirateTask.ID]: aspirateTask,
          [dispenseTask.ID]: dispenseTask,
          [ejectTip.ID]: ejectTip,
        };

        newTaskIds.push(pickTipTask.ID, aspirateTask.ID, dispenseTask.ID, ejectTip.ID);
        break;
      }
      default: {
        let taskId = task.ID;
        let taskNumber = 1;

        if (newTaskIds.length > 0) {
          taskId = newNormalizationTasks[newTaskIds[newTaskIds.length - 1]].ID + 1;
          taskNumber = newNormalizationTasks[newTaskIds[newTaskIds.length - 1]].number + 1;
        }

        pickTipTask = {
          ...task,
          ID: taskId,
          number: taskNumber,
        };
      }
    }
  }

  const taskBeforeNormIdx = Object.values(tasks).findIndex((task) => task.ID === firstTaskID);
  let taskBeforeNormalization = Object.values(newTasks).slice(0, taskBeforeNormIdx);
  taskBeforeNormalization = taskBeforeNormalization.reduce((rest, item) => ({...rest, [item.ID]: item}), {});

  const taskAfterNormIdx = taskBeforeNormIdx + totalTask;
  const tempTaskAfterNormalization = Object.values(newTasks).slice(taskAfterNormIdx, Object.keys(newTasks).length);

  let taskAfterNormalization = {};
  const taskIdChanges = {};

  if (tempTaskAfterNormalization.length > 0) {
    for (let i = 0; i < tempTaskAfterNormalization.length; i += 1) {
      const newId = newTaskIds[newTaskIds.length - 1] + 1 + i;
      taskIdChanges[tempTaskAfterNormalization[i].ID] = newId;

      taskAfterNormalization = {
        ...taskAfterNormalization,
        [newId]: {
          ...tempTaskAfterNormalization[i],
          ID: newId,
        },
      };
    }

    // adjust altered task IDs in cards
    Object.values(newCards).forEach((card) => {
      if (card.ID !== cardId) {
        const cardTask = card.tasks;
        cardTask.forEach((task, index) => {
          cardTask[index] = taskIdChanges[task] ? taskIdChanges[task] : cardTask[index];
        });
      }
    });
  }

  newTasks = {
    ...taskBeforeNormalization,
    ...newNormalizationTasks,
    ...taskAfterNormalization,
  };

  newCards[cardId] = {
    ...newCards[cardId],
    tasks: newTaskIds,
  };

  return [newCards, newTasks];
};

export const checkNormalizationTasks = (cards, tasks) => (dispatch) => {
  let newCards = cards;
  let newTasks = tasks;
  const normalizationCards = Object.values(cards).filter((card) => card.type === CardType.NORMALIZATION);

  dispatch(showLoading('Please wait, system is processing...'));

  for (let i = 0; i < normalizationCards.length; i += 1) {
    const card = normalizationCards[i];
    // 1536 is the number of max task.
    // 384 well * 4 task (pick tip, asp, disp, eject tip) = 1536
    if (card?.tasks?.length > 1536) {
      const [processedCards, processedTasks] = onReduceNormalizationTask(card.tasks, card.ID, newCards, newTasks);

      newCards = processedCards;
      newTasks = processedTasks;
    }
  }
  const result = {newCards, newTasks};

  dispatch(hideLoading());
  return result;
};

export const showFailedToUpdatePipettingValueModal = (key) => (dispatch) => {
  let title;
  let content;

  if (key === PipettingParameterFieldKey.DISPENSE_AT_WELL_SIDE) {
    title = parseMessage('protocolEditor.taskList.unableToDispense.modal.title');
    content = parseMessage('protocolEditor.taskList.unableToDispense.modal.content');
  } else if (key === PipettingParameterFieldKey.TIP_TOUCH_OFF) {
    title = parseMessage('protocolEditor.taskList.unableToTipTouchOff.modal.title');
    content = parseMessage('protocolEditor.taskList.unableToTipTouchOff.modal.content');
  }

  const body = (
    <>
      <div>{content}</div>
      <div className="modal-side-of-well__info-message">
        <span className="modal-side-of-well__icon icon-Info-Fill" />
        <span>
          <FormattedHTMLMessage id="protocolEditor.taskList.unableToDispense.modal.infoMessage" />
        </span>
      </div>
    </>
  );

  const button = [{label: parseMessage('protocolEditor.taskList.unableToDispense.modal.button')}];

  dispatch(showModalSimplified(title, body, button, {hideClose: false}));
};

export const getCalculatedRequiredTipsSingleCard = (card) => (_, getState) => {
  const {
    cardState,
    protocolState: {lastOpenedProtocol},
    configState,
    appState,
    runtimeState,
  } = getState();

  const {viewMode} = appState;
  const {tasks} = cardState;
  const trayType =
    viewMode === ViewMode.RUNTIME || !lastOpenedProtocol ? runtimeState.trayType : lastOpenedProtocol.trayType;
  const {maxAspirateVolume, maxAspirateHighVolume} = configState;

  const maxAspirateVolumeCalculated =
    trayType === TrayType.HIGH_VOLUME_CADDY ? maxAspirateHighVolume : maxAspirateVolume;

  const cardTasks = getTasksInCard(card, tasks);
  const newRequiredTips = calculateRequiredTipsForCard(card, cardTasks, maxAspirateVolumeCalculated);

  return newRequiredTips;
};
